import { useEffect, useState } from "react";

/**
 * Ilustracje 3D wygenerowane przez Gemini (`app/tools/generate_art.py`).
 *
 * Nie są dekoracją: każda odpowiada etapowi procesu, więc widok po lewej zmienia
 * się razem z postępem i mówi bez słów, na czym teraz jesteśmy. Dlatego mają
 * puste `alt` i `aria-hidden` — treść niosą etykiety etapów, nie obrazek.
 *
 * Obok każdego `.webp` leży `.webm` z minimalnym ruchem (obiekt unosi się,
 * refleks wędruje po lakierze). Wideo jest wzbogaceniem, nie warunkiem:
 * statyczny render zostaje plakatem i jedyną treścią, gdy plik się nie
 * wczyta, silnik nie zna WebM — albo gdy człowiek prosi system o mniej ruchu.
 */

export type ArtName =
  | "start"
  | "tozsamosc"
  | "legalizacja"
  | "dane"
  | "podpis"
  | "gotowe"
  | "start-link";

const KNOWN: ArtName[] = [
  "start", "tozsamosc", "legalizacja", "dane", "podpis", "gotowe", "start-link",
];

/** Zapytanie o ograniczony ruch — rozstrzygane w kodzie, nie w CSS.
    Media query w arkuszu nie wystarczy: `<video autoplay>` ukryte stylem
    i tak pobrałoby plik i odtwarzało pętlę, a pętla ruchu u osoby
    z zaburzeniami przedsionkowymi potrafi wywołać mdłości. */
const OGRANICZONY_RUCH = "(prefers-reduced-motion: reduce)";

function ruchDozwolony(): boolean {
  return typeof window !== "undefined" && !window.matchMedia(OGRANICZONY_RUCH).matches;
}

export function Art({
  name,
  size = 200,
  className,
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  // Preferencję czytamy przed pierwszym renderem, żeby wideo w ogóle nie
  // weszło do drzewa — i nasłuchujemy zmiany, bo na macOS przełącza się ją
  // systemowo w trakcie działania strony.
  const [ruch, setRuch] = useState(ruchDozwolony);
  // Jedna porażka wideo przełącza na statykę do końca sesji — nie ma sensu
  // próbować siedmiu plików po kolei na silniku, który nie zna WebM.
  const [padlo, setPadlo] = useState(false);

  useEffect(() => {
    const zapytanie = window.matchMedia(OGRANICZONY_RUCH);
    const nasluch = () => setRuch(!zapytanie.matches);
    zapytanie.addEventListener("change", nasluch);
    return () => zapytanie.removeEventListener("change", nasluch);
  }, []);

  if (!KNOWN.includes(name as ArtName)) return null;

  if (!ruch || padlo) {
    return (
      <img
        className={className}
        src={`/art/${name}.webp`}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    );
  }

  return (
    // `preload="none"` to odpowiednik `loading="lazy"` obrazka: siedem pętli
    // waży razem ~172 kB i nie wolno ich ciągnąć naraz na starcie — plakat
    // z `poster` trzyma kadr, aż autoplay poprosi o swój jeden plik.
    // `key` wymusza świeży element przy zmianie etapu: przeładowanie `src`
    // na żywym `<video>` potrafi zostawić klatkę poprzedniej pętli.
    <video
      key={name}
      className={className}
      src={`/art/${name}.webm`}
      poster={`/art/${name}.webp`}
      width={size}
      height={size}
      muted
      loop
      playsInline
      autoPlay
      preload="none"
      aria-hidden="true"
      tabIndex={-1}
      disablePictureInPicture
      onError={() => setPadlo(true)}
      draggable={false}
    />
  );
}
